import { useCallback, useEffect, useState } from "react";
import type { PublicScanCreateResultV1, PublicScanStatusV1 } from "@enrein/shared-contracts";
import type { StudioApiClient } from "../../../api.js";
import { GuidedButton, GuidedFlowCopy } from "../primitives/GuidedPrimitives.js";

export function GuidedScanView({
  api,
  onScanComplete,
  onNavigate,
}: {
  readonly api: StudioApiClient;
  readonly onScanComplete: (publicReportRef: string) => void;
  readonly onNavigate: (view: string) => void;
}) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scan, setScan] = useState<PublicScanCreateResultV1 | null>(null);
  const [status, setStatus] = useState<PublicScanStatusV1 | null>(null);

  const pollStatus = useCallback(
    async (scanId: string) => {
      const next = await api.getPublicScanStatus(scanId);
      setStatus(next);
      return next;
    },
    [api],
  );

  useEffect(() => {
    if (!scan) return;
    if (status?.status === "COMPLETE" || status?.status === "FAILED") return;

    let cancelled = false;
    const tick = async () => {
      try {
        const next = await pollStatus(scan.scanId);
        if (!cancelled && next.status === "COMPLETE") {
          onScanComplete(next.publicReportRef);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : String(caught));
        }
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), 2500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [onScanComplete, pollStatus, scan, status?.status]);

  async function runScan() {
    const trimmed = url.trim();
    if (!trimmed) {
      setError("Enter a website URL to scan.");
      return;
    }
    setBusy(true);
    setError(null);
    setScan(null);
    setStatus(null);
    try {
      const created = await api.createPublicScan(trimmed);
      setScan(created);
      setStatus(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="enrein-guided-view is-active" id="view-scan" aria-label="Scan existing site">
      <div className="enrein-guided-identity-strip">
        <GuidedButton variant="subtle" onClick={() => onNavigate("home")}>
          ← Home
        </GuidedButton>
      </div>

      <GuidedFlowCopy title="Scan existing site" kicker="Relaunch opportunity">
        <p className="enrein-guided-muted">
          Scores are for agency discussion only — not a client contract or ranking guarantee.
        </p>
      </GuidedFlowCopy>

      <label className="enrein-guided-field">
        <span>Website URL</span>
        <input
          className="enrein-guided-scan-input"
          aria-label="Website URL"
          value={url}
          disabled={busy}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
        />
      </label>

      <div className="enrein-guided-btn-row">
        <GuidedButton variant="gold" disabled={busy} onClick={() => void runScan()}>
          {busy ? "Starting scan…" : "Run scan"}
        </GuidedButton>
        <GuidedButton variant="line" onClick={() => onNavigate("scan-opportunities")}>
          View opportunities
        </GuidedButton>
      </div>

      {scan ? (
        <div className="enrein-guided-scan-status">
          <p>
            <strong>Report ID:</strong> <span className="enrein-guided-mono">{scan.publicReportRef}</span>
          </p>
          <p>
            <strong>Status:</strong> {status?.status ?? scan.status}
            {status?.score != null ? ` · Score ${status.score}` : ""}
          </p>
          {status?.status === "COMPLETE" ? (
            <p className="enrein-guided-muted">Scan complete — claim added to opportunities.</p>
          ) : null}
          {status?.failureReason ? <p className="enrein-guided-validation">{status.failureReason}</p> : null}
        </div>
      ) : null}

      {error ? <p className="enrein-guided-validation">{error}</p> : null}
    </section>
  );
}
